import { useMemo } from 'react';
import { useTranslation } from '@frontend/i18n';
import { useMerchantPayoutsPausingStatus, useShouldShowPayoutsPausingAlert } from '@frontend/payments';
import { useMerchants } from '@frontend/payments-hooks';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as paymentsFeatureDefinition } from './feature-definitions';

export const usePaymentsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const { selectedLocationIds } = useAppScopeStore();

  const firstLocationId = selectedLocationIds[0];
  const { hasAccountAlert: hasPaymentAccountAlert } = useMerchants(firstLocationId)['firstLocation'];
  const { anyLocationHasPayments } = useMerchants(selectedLocationIds);
  const { status: paymentAccountAlertType } = useMerchantPayoutsPausingStatus(firstLocationId);
  const shouldShowPaymentsAccountAlert = useShouldShowPayoutsPausingAlert(firstLocationId);
  const paymentsItems = paymentsFeatureDefinition.items;
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const showInsurancePaymentTabs = getFeatureFlagValue('Insurance-EOB-management');

  const subNavItems = useMemo(() => {
    const getHasPaymentsNavItems = () => {
      return [
        {
          ...paymentsItems['/invoices'],
          label: t('Requests'),
          relativePath: '/invoices',
        },
        {
          ...paymentsItems['/payouts'],
          label: t('Payouts'),
          relativePath: '/payouts',
        },
        {
          ...paymentsItems['/payment-plans'],
          label: t('Payment Plans'),
          relativePath: '/payment-plans',
        },
        {
          ...paymentsItems['/eob-management'],
          label: t('EOB Management'),
          relativePath: '/eob-management',
          show: showInsurancePaymentTabs,
        },
      ];
    };

    const getNoPaymentsNavItems = () => {
      return [
        {
          label: t('Get Payments'),
          /**
           * Give it the same relative path as "Requests" (above), so that
           * when switching accounts, users don't end up on a stranded URL.
           */
          relativePath: '/invoices',
          trackingId: 'nav-payments-signup',
          description:
            'Start accepting payments by setting up your Weave Payments account. Quick and easy onboarding to begin processing transactions.',
        },
      ];
    };

    return createSubNavItems(anyLocationHasPayments ? getHasPaymentsNavItems() : getNoPaymentsNavItems());
  }, [t, anyLocationHasPayments, showInsurancePaymentTabs, paymentsItems]);

  return useMemo(
    () =>
      createNavItem('payments', {
        hasCount: !!hasPaymentAccountAlert && shouldShowPaymentsAccountAlert,
        hasCountColor: paymentAccountAlertType === 'error' ? theme.colors.critical50 : theme.colors.warning50,
        iconName: 'payments',
        label: t('Payments'),
        root: '/payments',
        subNavItems,
        settings: { path: '/payments', label: '' },
      }),
    [t, hasPaymentAccountAlert, shouldShowPaymentsAccountAlert, paymentAccountAlertType, subNavItems]
  );
};
